import React, { useState, useEffect, Fragment, useRef } from "react";
import {
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import WhatWeDo from "./signin-components/what-we-do";
import WhomWeServe from "./signin-components/whom-we-serve";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { login } from "../redux/login/action";
import useLocales from "../hooks/useLocales";
import { toast } from "react-toastify";
import { loginWithSAML } from "../api";
import { useLocation } from "react-router-dom";

const schema = yup.object().shape({
  email: yup.string().email("Please enter a valid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

const Logins = () => {
  const dispatch = useDispatch();
  const { t } = useLocales();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    errors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [togglePassword, setTogglePassword] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const [dynamicCaptcha, setDynamicCaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false); // State to toggle password visibility
  const formDataRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.showPasswordExpiredModal) {
      setShowPasswordModal(true);
      formDataRef.current = location.state.expiredUserData;

      // Clear state so modal doesn't reopen on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    generateDynamicCaptcha();
    if (localStorage.getItem("isRememberMe") === "true") {
      navigate("/rememberMe");
    }
    if (localStorage.getItem("token")) {
      navigate("/user/role");
    }
  }, []);

  const generateDynamicCaptcha = () => {
    const newCaptcha = Math.random().toString(36).substring(2, 7);
    setDynamicCaptcha(newCaptcha);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    schema
      .validateAt(name, { [name]: value })
      .then(() => setError(name, ""))
      .catch((error) =>
        setError(name, {
          type: "manual",
          message: error.message,
        })
      );
  };

  const onSubmit = async (data) => {

    console.log("Form Data :", data);

    if (data.manualCaptcha !== dynamicCaptcha) {
      setCaptchaError("Invalid captcha. Please try again.");
      return;
    }

    const userDetails = {
      email: data.email,
      password: data.password,
      newPassword: "", // not overriding
    };

    try {
      setLoading(true);
      dispatch(login(userDetails, navigate, remember, toast, setLoading, false, () => {}));
    } catch (error) {
      console.error(error);
    }
  };

  const handleCaptchaChange = () => {
    setCaptchaError("");
  };

  const handleNewPasswordSubmit = () => {

    if (newPassword.length < 8 || newPassword.length > 12) {
      setNewPasswordError("New Password update failed. Please ensure your password meets the required policy.");
      return;
    }

    const originalData = formDataRef.current;
    const userDetails = {
      email: originalData.email,
      password: originalData.password,
      newPassword,
    };

    setShowPasswordModal(false);
    setNewPassword("");
    setNewPasswordError("");

    try {
      setLoading(true);
      dispatch(login(userDetails, navigate, remember, toast, setLoading, false, () => {}));
    } catch (error) {
      console.error(error);
    }
  };

  const handleSSOLogin = async () => {
    try {
      setLoading(true);
      const response = await loginWithSAML();
      if (response.status === 200) {
        toast.success("Login successful!");
        navigate("/user/role");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } catch (error) {
      console.error("SSO login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Container fluid style={{ background: "#f8f9fe" }}>
        <Row>
          <Col xl="12" className="p-0">
            <Row className="no-gutters">
              <Col xl="6">
                <Row>
                  <Col xl="3" className="pl-5 pt-3">
                    <a className="logo">
                      <img className="img-fluid for-light" src={require("../assets/images/logo/eai-logo-2.png")} alt="" />
                      <img className="img-fluid for-dark" src={require("../assets/images/logo/eai-logo-2.png")} alt="" />
                    </a>
                  </Col>
                  <Col xl="9" className="d-none d-lg-block">
                    <div className="b-center bg-size">
                      <WhatWeDo />
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col xl="6">
                <div className="login-card login-extra-css" style={{ background: "#f8f9fe", marginRight: "90px" }}>
                  <div className="login-main login-tab">
                    <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
                      <h4>{t("login")}</h4>
                      <hr />
                      <FormGroup>
                        <Label className="col-form-label">{t("email")}</Label>
                        <Input
                          className="form-control"
                          type="email"
                          name="email"
                          onChange={handleChange}
                          placeholder="Enter Email"
                          innerRef={register}
                        />
                        {errors.email && <div className="error-message">{errors.email.message}</div>}
                      </FormGroup>

                      <FormGroup>
                        <Label className="col-form-label">{t("password")}</Label>
                        <Input
                          className="form-control"
                          type={togglePassword ? "text" : "password"}
                          name="password"
                          innerRef={register}
                          onChange={handleChange}
                        />
                        <div className="show-hide" onClick={() => setTogglePassword(!togglePassword)}>
                          <span className={togglePassword ? "" : "show"}></span>
                        </div>
                        {errors.password && <div className="error-message">{errors.password.message}</div>}
                      </FormGroup>

                      <Row style={{ paddingTop: "15px"}}>
                        <Col xl="6" sm="12">
                          <Input
                            className="form-control"
                            type="text"
                            name="manualCaptcha"
                            innerRef={register}
                            placeholder="Enter captcha"
                            onChange={handleCaptchaChange}
                          />
                        </Col>
                        <Col xl="4" sm="6">
                          <div
                            style={{
                              textAlign: "right",
                              fontWeight: "bold",
                              fontSize: "20px",
                              userSelect: "none",
                              cursor: "pointer",
                            }}
                          >
                            {dynamicCaptcha}
                          </div>
                        </Col>
                        <Col xl="2" sm="6">
                          <Button color="primary" size="sm" style={{paddingLeft: "12px", paddingRight: "12px"}} onClick={generateDynamicCaptcha}>
                            <i className="fa fa-refresh"></i>
                          </Button>
                        </Col>
                      </Row>
                      {captchaError && <div className="error-message">{captchaError}</div>}

                      <FormGroup className="d-flex justify-content-between align-items-center mt-2">
                        <div className="checkbox ml-1">
                          <input
                            id="checkbox1"
                            type="checkbox"
                            onClick={() => setRemember(!remember)}
                            ref={register}
                            name="rememberPassword"
                          />
                          <Label className="text-muted" htmlFor="checkbox1">
                            {t("remember_password")}
                          </Label>
                        </div>
                        <Link to={`${process.env.PUBLIC_URL}/forgot-password`}>
                          {t("forgot_password")}
                        </Link>
                      </FormGroup>

                      <div className="form-group mb-0">
                        <Button
                          color="primary"
                          className="btn-block"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "PLEASE WAIT..." : t("sign_in")}
                        </Button>
                      </div>

                      <h6 className="text-muted mt-4 or">{t("or_sign_in_with")}</h6>
                      <div className="social mt-4">
                        <Button className="btn-block" color="light" onClick={handleSSOLogin}>
                          <img src={require("../assets/images/logo/Microsoft_Logo_128px.png")} width={25} alt="" /> EAI SSO
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <Container fluid className="d-none d-lg-block" style={{ background: "#f8f9fe" }}>
        <Row>
          <Col xl="1" />
          <Col xl="10">
            <WhomWeServe />
          </Col>
          <Col xl="1" />
        </Row>
      </Container>

      {/* Modal for new password input */}
      <Modal isOpen={showPasswordModal} toggle={() => {
        setShowPasswordModal(false);
        setNewPassword("");
        setNewPasswordError("");
      }}>
        <ModalHeader toggle={() => {
          setShowPasswordModal(false);
          setNewPassword("");
          setNewPasswordError("");
          window.location.reload();
        }}>
          Password Update
        </ModalHeader>
        <ModalBody>
          <p>Your password has expired. Kindly create a new password in accordance with the updated password policy</p>
          <p><strong>PASSWORD POLICY :-</strong>  (8-12 chars & Mininmum 1 upper, 1 lower, 1 number, 1 special char)</p>
          <FormGroup>
            <Label>New Password</Label>
            <div className="input-group">
              <Input
                type={showNewPassword ? "text" : "password"} // Toggle input type
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setNewPasswordError("");
                }}
                placeholder="Enter new password"
              />
              <div className="input-group-append">
                <Button
                  color="secondary"
                  onClick={() => setShowNewPassword(!showNewPassword)} // Toggle visibility
                >
                  {showNewPassword ? "Hide" : "Show"}
                </Button>
              </div>
            </div>
            {newPasswordError && <div className="error-message">{newPasswordError}</div>}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleNewPasswordSubmit}>
            Submit New Password
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Logins;